import type React from 'react';
import type { ApplicationRoute } from 'contracts';
import type { SelectOptions } from 'pages/shared/form/select';

import { SignupStep, StepRouteParam, ApiStep } from './contracts';
import createAccountId from './steps/create-account/@@global/id';
import accountTypeId from './steps/account-type/@@global/id';
import emailConfirmationId from './steps/email-confirmation/@@global/id';
import personalDataId from './steps/personal-data/@@global/id';
import documentsId from './steps/documents/@@global/id';

const stepRouteParamToModuleId: Record<StepRouteParam, keyof ApplicationRoute> = {
  [StepRouteParam.NewAccount]: createAccountId as keyof ApplicationRoute,
  [StepRouteParam.AccountType]: accountTypeId as keyof ApplicationRoute,
  [StepRouteParam.CategoryAndDisplayName]: accountTypeId as keyof ApplicationRoute,
  [StepRouteParam.ConfirmEmailAddress]: emailConfirmationId as keyof ApplicationRoute,
  [StepRouteParam.OwnerDetails]: personalDataId as keyof ApplicationRoute,
  [StepRouteParam.SimplifiedOwnerDetails]: personalDataId as keyof ApplicationRoute,
  [StepRouteParam.CreateModelProfile]: personalDataId as keyof ApplicationRoute,
  [StepRouteParam.IdData]: documentsId as keyof ApplicationRoute,
  [StepRouteParam.UploadDocuments]: documentsId as keyof ApplicationRoute,
};

const apiToRoute: Record<ApiStep, StepRouteParam> = {
  [ApiStep.CreateAccount]: StepRouteParam.NewAccount,
  [ApiStep.AccountType]: StepRouteParam.AccountType,
  [ApiStep.CategoryAndDisplayName]: StepRouteParam.CategoryAndDisplayName,
  [ApiStep.EmailConfirmation]: StepRouteParam.ConfirmEmailAddress,
  [ApiStep.PersonalDetails]: StepRouteParam.OwnerDetails,
  [ApiStep.PersonalData]: StepRouteParam.CreateModelProfile,
  [ApiStep.DocumentDetails]: StepRouteParam.IdData,
  [ApiStep.UploadDocuments]: StepRouteParam.UploadDocuments,
  [ApiStep.AmateurDetails]: StepRouteParam.SimplifiedOwnerDetails,
  [ApiStep.OranumPersonalDetails]: StepRouteParam.OwnerDetails,
};

const moduleToUrlParam: Record<SignupStep, StepRouteParam[]> = {
  [SignupStep.CreateAccount]: [StepRouteParam.NewAccount],
  [SignupStep.EmailConfirmation]: [StepRouteParam.ConfirmEmailAddress],
  [SignupStep.AccountType]: [StepRouteParam.AccountType, StepRouteParam.CategoryAndDisplayName],
  [SignupStep.PersonalData]: [
    StepRouteParam.SimplifiedOwnerDetails,
    StepRouteParam.OwnerDetails,
    StepRouteParam.CreateModelProfile,
  ],
  [SignupStep.Documents]: [StepRouteParam.IdData, StepRouteParam.UploadDocuments],
};

const getModuleIdFromStepRouteParam = (param: string): keyof ApplicationRoute | undefined => {
  return stepRouteParamToModuleId[param];
};

/**
 * Get Module Owner from given step route param
 * @param param - step route param
 */
const getModuleOwnerFromUrlParam = (param: string | undefined): SignupStep | undefined => {
  return Object.keys(moduleToUrlParam).find((module) =>
    moduleToUrlParam[module as SignupStep].includes(param as StepRouteParam)
  ) as SignupStep;
};

/**
 * Get step route param from given API stepId
 * @param stepId - API response "step_id"
 */
const getRouteFromApi = (stepId: string): StepRouteParam | 'in-review' | undefined => {
  if (stepId === 'in-review') {
    return stepId;
  }

  return apiToRoute[stepId as ApiStep];
};

/**
 * Get number of days in a provided month and year
 * @param {number} [year]
 * @param {number} [month]
 * @returns {number}
 *
 * @example
 *  // Current date will be by default (January 2022)
 *  getDaysInMonth() -> 31
 *
 *  // February has 28 days in 2022
 *  getDaysInMonth(2022, 2) -> 28
 *
 *  // February has 29 days in 2024
 *  getDaysInMonth(2022, 2) -> 28
 *
 *  // May has 31 days
 *  getDaysInMonth(2022, 5) -> 31
 *
 * // September has 30 days
 *  getDaysInMonth(2022, 9) -> 30
 */
const getDaysInMonth = (year?: number, month?: number): number => {
  if (!year || !month) {
    return 31;
  }

  return new Date(year, month, 0).getDate();
};

const onTriggerPreventDefault = <T>(callback: () => void): ((event: React.MouseEvent<T>) => void) => {
  return (event: React.MouseEvent<T>): void => {
    event.preventDefault();

    callback();
  };
};

const mapDataToSelectOptions = <T extends object>(
  source: Array<T> | undefined,
  key: keyof T,
  value: keyof T
): SelectOptions => {
  return (source ?? []).reduce(
    (acc, entry) => [...acc, [entry[key]?.toString(), entry[value]?.toString()]] as SelectOptions,
    [] as SelectOptions
  );
};

export {
  getModuleOwnerFromUrlParam,
  getModuleIdFromStepRouteParam,
  getRouteFromApi,
  getDaysInMonth,
  onTriggerPreventDefault,
  mapDataToSelectOptions,
};
